import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Encapsulates the "stage detail + admin actions" state machine:
 *  - loads the stage
 *  - runs open/close/tabulate/delete actions with a shared busy flag
 *  - manages the tiebreaker modal state
 *
 * Extracted from AdminStageDetail.jsx to reduce the component's cyclomatic
 * complexity (see Code Quality Report — Phase 5.5).
 */
export function useStageDetail(id, navigate) {
  const [stage, setStage] = useState(null);
  const [action, setAction] = useState(null); // 'open'|'close'|'tabulate'|'delete'
  const [busy, setBusy] = useState(false);
  const [tieOpen, setTieOpen] = useState(false);
  const [tieForm, setTieForm] = useState({
    winner_band_id: '',
    tiebreaker_method: 'decisao_organizacao',
    justification: '',
  });

  const load = useCallback(async () => {
    const r = await api.get(`/admin/stages/${id}`);
    setStage(r.data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const _runDelete = useCallback(async () => {
    await api.delete(`/admin/stages/${id}`);
    toast.success('Etapa cancelada');
    // If the stage was fully removed, navigating back is safer than reloading a 404.
    if (navigate) navigate('/admin/etapas');
    else load();
  }, [id, navigate, load]);

  const _runVotingAction = useCallback(async (endpoint) => {
    const r = await api.post(`/admin/stages/${id}/${endpoint}`);
    if (endpoint === 'tabulate' && r.data.tie) {
      toast.warning('Empate! Defina o desempate.');
      setTieOpen(true);
    } else {
      toast.success('Feito!');
    }
    load();
  }, [id, load]);

  const perform = useCallback(async () => {
    if (!action) return;
    setBusy(true);
    try {
      if (action === 'delete') {
        await _runDelete();
      } else {
        const endpointByAction = { open: 'open-voting', close: 'close-voting', tabulate: 'tabulate' };
        const ep = endpointByAction[action];
        if (ep) await _runVotingAction(ep);
      }
      setAction(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setBusy(false);
    }
  }, [action, _runDelete, _runVotingAction]);

  const submitTie = useCallback(async () => {
    if (!tieForm.winner_band_id || !tieForm.justification.trim()) {
      toast.error('Escolha a vencedora e justifique.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/stages/${id}/set-winner`, tieForm);
      toast.success('Vencedora definida.');
      setTieOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setBusy(false);
    }
  }, [id, tieForm, load]);

  return {
    stage, action, busy, tieOpen, tieForm,
    setAction, setTieOpen, setTieForm,
    load, perform, submitTie,
  };
}
